import { InvoiceMode, InvoiceStatus } from "@prisma/client";
import { forwardRef, PropsWithChildren } from "react";

import { Badge, BadgeProps, Flex, MultiSelect, type MultiSelectProps, Select, type SelectProps, Text } from "~/ui";
import { enumTermToText, transforms } from "~/utils/strings";

const statusToColors: Record<InvoiceStatus, string> = {
  DRAFT: "gray",
  POSTED: "blue",
  PARTIALLY_PAID: "yellow",
  PAID: "green",
  VOID: "gray",
};

const termToText = new Map<InvoiceStatus, string>();
Object.keys(InvoiceStatus).forEach(v => termToText.set(v as InvoiceStatus, enumTermToText(v, transforms("Po", "PO"))));

const statusOptions = Array.from(termToText).map(([term, text]) => ({ value: term, label: text }));

export const InvoiceStatusSelect = (props: Omit<SelectProps, "data">) => (
  <Select placeholder="Status" searchable clearable {...props} data={statusOptions} />
);

export const InvoiceStatusMultiSelect = (props: Omit<MultiSelectProps, "data">) => (
  <MultiSelect placeholder="Status" searchable clearable {...props} data={statusOptions} />
);

type InvoiceStatusDisplayProps = Omit<BadgeProps, "color"> & {
  status: InvoiceStatus;
};
export const InvoiceStatusDisplay = (props: InvoiceStatusDisplayProps) => (
  <Badge style={{ textTransform: "none" }} {...props} color={statusToColors[props.status]}>
    {termToText.get(props.status)}
  </Badge>
);

const modeToText = new Map<InvoiceMode, string>();
Object.keys(InvoiceMode).forEach(v => modeToText.set(v as InvoiceMode, enumTermToText(v)));

const descriptions = {
  [InvoiceMode.STANDARD]: "Invoice ordered items using the available order invoicing actions.",
  [InvoiceMode.CONSIGNMENT]: "Order items can be partially invoiced over time using multiple invoices.",
};

const modeOptions = Array.from(modeToText).map(([term, text]) => ({ value: term, label: text, description: descriptions[term] }));

interface InvoiceModeItemProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  description: string;
}
const InvoiceModeItem = forwardRef<HTMLDivElement, InvoiceModeItemProps>(({ label, description, ...others }: InvoiceModeItemProps, ref) => (
  <Flex {...others} ref={ref} direction="column">
    <Text weight="bold">{label}</Text>
    <Text size="xs" style={{ wordBreak: "break-word" }}>
      {description}
    </Text>
  </Flex>
));
InvoiceModeItem.displayName = "InvoiceModeItem";

export const InvoiceModeSelect = (props: Omit<SelectProps, "data">) => (
  <Select placeholder="Invoice Mode" itemComponent={InvoiceModeItem} {...props} data={modeOptions} />
);

export const InvoiceModeMultiSelect = (props: Omit<MultiSelectProps, "data">) => (
  <MultiSelect placeholder="Invoice Mode" searchable clearable {...props} data={modeOptions} />
);

const modeToColors: Record<InvoiceMode, string> = {
  STANDARD: "gray",
  CONSIGNMENT: "blue",
};
type InvoiceModeDisplayProps = Omit<BadgeProps, "color"> &
  PropsWithChildren<{
    invoiceMode: InvoiceMode;
  }>;
export const InvoiceModeDisplay = (props: InvoiceModeDisplayProps) => {
  const { children, invoiceMode, ...rest } = props;
  return (
    <Badge style={{ textTransform: "none" }} {...rest} color={modeToColors[invoiceMode]}>
      {modeToText.get(invoiceMode)} {children}
    </Badge>
  );
};
